import { isFunction, isString, stringValue } from 'vega-util';
import { isCountingAggregateOp } from '../../aggregate';
import { isBinned, isBinning } from '../../bin';
import { getMainRangeChannel, X, X2, Y, Y2 } from '../../channel';
import { binRequiresRange, format, getFieldDef, hasConditionalFieldDef, isFieldDef, isTypedFieldDef, isValueDef, title, vgField } from '../../channeldef';
import { forEach } from '../../encoding';
import * as log from '../../log';
import { isPathMark } from '../../mark';
import { hasDiscreteDomain, isContinuousToContinuous, ScaleType } from '../../scale';
import { QUANTITATIVE } from '../../type';
import { contains, getFirstDefined } from '../../util';
import { binFormatExpression, formatSignalRef, getMarkConfig } from '../common';

function midPointWithPositionInvalidTest(params) {
  var channel = params.channel,
      channelDef = params.channelDef,
      mark = params.mark,
      scale = params.scale;
  var ref = midPoint(params); // Wrap to check if the positional value is invalid, if so, plot the point on the min value

  if ( // Only this for field def without counting aggregate (as count wouldn't be null)
  isFieldDef(channelDef) && !isCountingAggregateOp(channelDef.aggregate) && // and only for continuous scale without zero (otherwise, null / invalid will be interpreted as zero, which doesn't cause layout problem)
  scale && isContinuousToContinuous(scale.get('type')) && scale.get('zero') === false) {
    return wrapPositionInvalidTest({
      fieldDef: channelDef,
      channel: channel,
      mark: mark,
      ref: ref
    });
  }

  return ref;
}

function wrapPositionInvalidTest(_ref) {
  var fieldDef = _ref.fieldDef,
      channel = _ref.channel,
      mark = _ref.mark,
      ref = _ref.ref;

  if (!isPathMark(mark)) {
    // Only do this for non-path mark (as path marks will already use "defined" to skip points)
    return [fieldInvalidTestValueRef(fieldDef, channel), ref];
  }

  return ref;
}

export function fieldInvalidTestValueRef(fieldDef, channel) {
  var test = fieldInvalidPredicate(fieldDef, true);
  var mainChannel = getMainRangeChannel(channel);
  var zeroValueRef = mainChannel === 'x' ? {
    value: 0
  } : {
    field: {
      group: 'height'
    }
  };
  return Object.assign({
    test: test
  }, zeroValueRef);
}
export function fieldInvalidPredicate(field) {
  var invalid = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : true;
  field = isString(field) ? field : vgField(field, {
    expr: 'datum'
  });
  var op = invalid ? '||' : '&&';
  var eq = invalid ? '===' : '!==';
  return "".concat(field, " ").concat(eq, " null ").concat(op, " ").concat(invalid ? '' : '!', "isNaN(").concat(field, ")");
} // TODO: we need to find a way to refactor these so that scaleName is a part of scale
// but that's complicated.  For now, this is a huge step moving forward.

/**
 * @return Vega ValueRef for normal x- or y-position without projection
 */

export function position(params) {
  var channel = params.channel,
      channelDef = params.channelDef,
      scaleName = params.scaleName,
      stack = params.stack,
      offset = params.offset;

  if (isFieldDef(channelDef) && stack && channel === stack.fieldChannel) {
    // x or y use stack_end so that stacked line's point mark use stack_end too.
    return fieldRef(channelDef, scaleName, {
      suffix: 'end'
    }, {
      offset: offset
    });
  }

  return midPointWithPositionInvalidTest(params);
}
/**
 * @return Vega ValueRef for normal x2- or y2-position without projection
 */

export function position2(_ref2) {
  var channel = _ref2.channel,
      channelDef = _ref2.channelDef,
      channel2Def = _ref2.channel2Def,
      scaleName = _ref2.scaleName,
      scale = _ref2.scale,
      stack = _ref2.stack,
      mark = _ref2.mark,
      offset = _ref2.offset,
      defaultRef = _ref2.defaultRef;

  if (isFieldDef(channelDef) && stack && // If fieldChannel is X and channel is X2 (or Y and Y2)
  channel.charAt(0) === stack.fieldChannel.charAt(0)) {
    return fieldRef(channelDef, scaleName, {
      suffix: 'start'
    }, {
      offset: offset
    });
  }

  return midPointWithPositionInvalidTest({
    channel: channel,
    channelDef: channel2Def,
    scaleName: scaleName,
    scale: scale,
    stack: stack,
    mark: mark,
    offset: offset,
    defaultRef: defaultRef
  });
}
export function getOffset(channel, markDef) {
  var offsetChannel = channel + 'Offset'; // Need to cast as the type can't be inferred automatically
  // TODO: in the future read from encoding channel too

  var markDefOffsetValue = markDef[offsetChannel];

  if (markDefOffsetValue) {
    return markDefOffsetValue;
  }

  return undefined;
}
/**
 * Value Ref for binned fields
 */

export function bin(_ref3) {
  var channel = _ref3.channel,
      fieldDef = _ref3.fieldDef,
      scaleName = _ref3.scaleName,
      mark = _ref3.mark,
      side = _ref3.side,
      offset = _ref3.offset;
  var binSuffix = side === 'start' ? undefined : 'end';
  var ref = fieldRef(fieldDef, scaleName, {
    binSuffix: binSuffix
  }, offset ? {
    offset: offset
  } : {});
  return wrapPositionInvalidTest({
    fieldDef: fieldDef,
    channel: channel,
    mark: mark,
    ref: ref
  });
}
export function fieldRef(fieldDef, scaleName, opt, mixins) {
  var ref = Object.assign({}, scaleName ? {
    scale: scaleName
  } : {}, {
    field: vgField(fieldDef, opt)
  });

  if (mixins) {
    var offset = mixins.offset,
        band = mixins.band;
    return Object.assign({}, ref, offset ? {
      offset: offset
    } : {}, band ? {
      band: band
    } : {});
  }

  return ref;
}
export function bandRef(scaleName) {
  var band = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : true;
  return {
    scale: scaleName,
    band: band
  };
}
/**
 * Signal that returns the middle of a bin from start and end field. Should only be used with x and y.
 */

function binMidSignal(_ref4) {
  var scaleName = _ref4.scaleName,
      fieldDef = _ref4.fieldDef,
      fieldDef2 = _ref4.fieldDef2,
      offset = _ref4.offset,
      band = _ref4.band;
  band = getFirstDefined(band, 0.5);
  var start = vgField(fieldDef, {
    expr: 'datum'
  });
  var end = fieldDef2 !== undefined ? vgField(fieldDef2, {
    expr: 'datum'
  }) : vgField(fieldDef, {
    binSuffix: 'end',
    expr: 'datum'
  });
  return Object.assign({
    signal: "scale(\"".concat(scaleName, "\", ").concat(band, " * ").concat(start, " + ").concat(1 - band, " * ").concat(end, ")")
  }, offset ? {
    offset: offset
  } : {});
}
/**
 * @returns {VgValueRef} Value Ref for xc / yc or mid point for other channels.
 */


export function midPoint(_ref5) {
  var channel = _ref5.channel,
      channelDef = _ref5.channelDef,
      channel2Def = _ref5.channel2Def,
      scaleName = _ref5.scaleName,
      scale = _ref5.scale,
      stack = _ref5.stack,
      offset = _ref5.offset,
      defaultRef = _ref5.defaultRef;

  // TODO: datum support
  if (channelDef) {
    /* istanbul ignore else */
    if (isFieldDef(channelDef)) {
      if (isTypedFieldDef(channelDef)) {
        if (isBinning(channelDef.bin)) {
          // Use middle only for x an y to place marks in the center between start and end of the bin range.
          // We do not use the mid point for other channels (e.g. size) so that properties of legends and marks match.
          if (contains([X, Y], channel) && channelDef.type === QUANTITATIVE) {
            if (stack && stack.impute) {
              // For stack, we computed bin_mid so we can impute.
              return fieldRef(channelDef, scaleName, {
                binSuffix: 'mid'
              }, {
                offset: offset
              });
            } // For non-stack, we can just calculate bin mid on the fly using signal.


            return binMidSignal({
              scaleName: scaleName,
              fieldDef: channelDef,
              offset: offset
            });
          }

          return fieldRef(channelDef, scaleName, binRequiresRange(channelDef, channel) ? {
            binSuffix: 'range'
          } : {}, {
            offset: offset
          });
        } else if (isBinned(channelDef.bin)) {
          if (isFieldDef(channel2Def)) {
            return binMidSignal({
              scaleName: scaleName,
              fieldDef: channelDef,
              fieldDef2: channel2Def,
              offset: offset
            });
          } else {
            var channel2 = channel === X ? X2 : Y2;
            log.warn(log.message.channelRequiredForBinned(channel2));
          }
        }
      }

      if (scale) {
        var scaleType = scale.get('type');

        if (hasDiscreteDomain(scaleType)) {
          if (scaleType === 'band') {
            // For band, to get mid point, need to offset by half of the band
            return fieldRef(channelDef, scaleName, {
              binSuffix: 'range'
            }, {
              band: 0.5,
              offset: offset
            });
          }

          return fieldRef(channelDef, scaleName, {
            binSuffix: 'range'
          }, {
            offset: offset
          });
        }
      }

      return fieldRef(channelDef, scaleName, {}, {
        offset: offset
      }); // no need for bin suffix
    } else if (isValueDef(channelDef)) {
      var value = channelDef.value;
      var offsetMixins = offset ? {
        offset: offset
      } : {};
      return Object.assign({}, vgValueRef(channel, value), offsetMixins);
    } // If channelDef is neither field def or value def, it's a condition-only def.
    // In such case, we will use default ref.

  }

  return isFunction(defaultRef) ? defaultRef() : defaultRef;
}
/**
 * Convert special "width" and "height" values in Vega-Lite into Vega value ref.
 */

export function vgValueRef(channel, value) {
  if (contains(['x', 'x2'], channel) && value === 'width') {
    return {
      field: {
        group: 'width'
      }
    };
  } else if (contains(['y', 'y2'], channel) && value === 'height') {
    return {
      field: {
        group: 'height'
      }
    };
  }

  return {
    value: value
  };
}
export function tooltipForEncoding(encoding, config) {
  var _ref6 = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : {},
      reactiveGeom = _ref6.reactiveGeom;

  var keyValues = [];
  var usedKey = {};
  var toSkip = {};
  var expr = reactiveGeom ? 'datum.datum' : 'datum';
  var tooltipTuples = [];

  function add(fDef, channel) {
    var mainChannel = getMainRangeChannel(channel);
    var fieldDef = isTypedFieldDef(fDef) ? fDef : Object.assign({}, fDef, {
      type: encoding[mainChannel].type // for secondary field def, copy type from main channel

    });
    var key = title(fieldDef, config, {
      allowDisabling: false
    });
    var value = text(fieldDef, config, expr).signal;

    if (channel === 'x' || channel === 'y') {
      var channel2 = channel === 'x' ? 'x2' : 'y2';
      var fieldDef2 = getFieldDef(encoding[channel2]);

      if (isBinned(fieldDef.bin) && fieldDef2) {
        var startField = vgField(fieldDef, {
          expr: expr
        });
        var endField = vgField(fieldDef2, {
          expr: expr
        });
        value = binFormatExpression(startField, endField, format(fieldDef), config);
        toSkip[channel2] = true;
      }
    }

    tooltipTuples.push({
      channel: channel,
      key: key,
      value: value
    });
  }

  forEach(encoding, function (channelDef, channel) {
    if (isFieldDef(channelDef)) {
      add(channelDef, channel);
    } else if (hasConditionalFieldDef(channelDef)) {
      add(channelDef.condition, channel);
    }
  });

  for (var _i = 0, _tooltipTuples = tooltipTuples; _i < _tooltipTuples.length; _i++) {
    var _tooltipTuples$_i = _tooltipTuples[_i],
        channel = _tooltipTuples$_i.channel,
        key = _tooltipTuples$_i.key,
        value = _tooltipTuples$_i.value;

    if (!toSkip[channel] && !usedKey[key]) {
      keyValues.push("".concat(stringValue(key), ": ").concat(value));
      usedKey[key] = true;
    }
  }

  return keyValues.length ? {
    signal: "{".concat(keyValues.join(', '), "}")
  } : undefined;
}
export function text(channelDef, config) {
  var expr = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : 'datum';

  // text
  if (channelDef) {
    if (isValueDef(channelDef)) {
      return {
        value: channelDef.value
      };
    }

    if (isTypedFieldDef(channelDef)) {
      return formatSignalRef(channelDef, format(channelDef), expr, config);
    }
  }

  return undefined;
}
export function mid(sizeRef) {
  return Object.assign({}, sizeRef, {
    mult: 0.5
  });
}
export function positionDefault(_ref7) {
  var markDef = _ref7.markDef,
      config = _ref7.config,
      defaultRef = _ref7.defaultRef,
      channel = _ref7.channel,
      scaleName = _ref7.scaleName,
      scale = _ref7.scale,
      mark = _ref7.mark,
      checkBarAreaWithZero = _ref7.checkBarAreaWithoutZero;
  return function () {
    var mainChannel = getMainRangeChannel(channel);
    var definedValueOrConfig = getFirstDefined(markDef[channel], getMarkConfig(channel, markDef, config));

    if (definedValueOrConfig !== undefined) {
      return vgValueRef(channel, definedValueOrConfig);
    }

    if (isString(defaultRef)) {
      if (scaleName) {
        var scaleType = scale.get('type');

        if (contains([ScaleType.LOG, ScaleType.TIME, ScaleType.UTC], scaleType)) {
          // Log scales cannot have zero.
          // Zero in time scale is arbitrary, and does not affect ratio.
          // (Time is an interval level of measurement, not ratio).
          // See https://en.wikipedia.org/wiki/Level_of_measurement for more info.
          if (checkBarAreaWithZero && (mark === 'bar' || mark === 'area')) {
            log.warn(log.message.nonZeroScaleUsedWithLengthMark(mark, mainChannel, {
              scaleType: scaleType
            }));
          }
        } else {
          if (scale.domainDefinitelyIncludesZero()) {
            return {
              scale: scaleName,
              value: 0
            };
          }

          if (checkBarAreaWithZero && (mark === 'bar' || mark === 'area')) {
            log.warn(log.message.nonZeroScaleUsedWithLengthMark(mark, mainChannel, {
              zeroFalse: scale.explicit.zero === false
            }));
          }
        }
      }

      if (defaultRef === 'zeroOrMin') {
        return mainChannel === 'x' ? {
          value: 0
        } : {
          field: {
            group: 'height'
          }
        };
      } else {
        // zeroOrMax
        return mainChannel === 'x' ? {
          field: {
            group: 'width'
          }
        } : {
          value: 0
        };
      }
    }

    return defaultRef;
  };
}